/**
 * Video Reels Carousel Component
 *
 * A horizontally scrollable carousel of vertical (9:16) video cards with:
 * - CSS scroll-snap for smooth swiping
 * - IntersectionObserver-based autoplay (muted) when visible
 * - Tap/click to play/pause
 * - Mute/unmute toggle
 * - Navigation arrows and pagination dots
 * - Infinite loop option
 * - Carousel autoplay (auto-advance)
 * - Keyboard accessibility
 * - Lazy loading via IntersectionObserver
 */

import { Component } from '@theme/component';

class VideoReelsComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;

  /** @type {IntersectionObserver | undefined} */
  #lazyObserver;

  /** @type {number | undefined} */
  #autoAdvanceTimer;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {boolean} */
  #isUserInteracting = false;

  /** @type {number | null} Index of the card whose audio is currently unmuted, or null if all muted */
  #activeAudioIndex = null;

  /** @type {boolean} */
  #isScrollingProgrammatically = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    // Cache refs
    this._track = this.querySelector('[data-reels-track]');
    this._cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-reel-card]')]);
    this._dots = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-reel-dot]')]);
    this._prevBtn = this.querySelector('[data-reels-prev]');
    this._nextBtn = this.querySelector('[data-reels-next]');
    this._counter = this.querySelector('[data-reels-counter]');

    if (!this._track || this._cards.length === 0) return;

    // ---- Keyboard navigation ----
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.setAttribute('tabindex', '0');

    // ---- Pause carousel autoplay on hover/focus ----
    this.addEventListener('mouseenter', () => { this.#isUserInteracting = true; this.#stopAutoAdvance(); }, { signal });
    this.addEventListener('mouseleave', () => { this.#isUserInteracting = false; this.#startAutoAdvance(); }, { signal });
    this.addEventListener('focusin', () => { this.#isUserInteracting = true; this.#stopAutoAdvance(); }, { signal });
    this.addEventListener('focusout', () => { this.#isUserInteracting = false; this.#startAutoAdvance(); }, { signal });

    // ---- Track scroll events for snap-based index tracking ----
    let scrollTimeout;
    this._track.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.#onScrollEnd(), 100);
    }, { signal, passive: true });

    // ---- Visibility observer: autoplay/pause videos ----
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const card = /** @type {HTMLElement} */ (entry.target);
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#playVideo(card);
          } else {
            this.#pauseVideo(card);
          }
        }
      },
      { threshold: [0, 0.6] }
    );

    // ---- Lazy load observer ----
    this.#lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#loadVideo(/** @type {HTMLElement} */ (entry.target));
            this.#lazyObserver?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '200px' }
    );

    // Observe all cards
    this._cards.forEach((card) => {
      this.#visibilityObserver?.observe(card);
      this.#lazyObserver?.observe(card);
    });

    // ---- Document visibility ----
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._cards.forEach((card) => this.#pauseVideo(card));
        this.#stopAutoAdvance();
      } else {
        this.#startAutoAdvance();
      }
    }, { signal });

    // Initial state
    this.#updateControls();
    this.#updatePlaybackStates(); // Dynamically updates mute icons/states on load
    this.#startAutoAdvance();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#visibilityObserver?.disconnect();
    this.#lazyObserver?.disconnect();
    this.#stopAutoAdvance();
  }

  // =============== NATIVE DELEGATED EVENT HANDLERS ===============

  navigatePrev(event) {
    event.stopPropagation();
    this.#navigate(-1);
  }

  navigateNext(event) {
    event.stopPropagation();
    this.#navigate(1);
  }

  goToSlideFromDot(data, event) {
    event.stopPropagation();
    const index = parseInt(data.index, 10);
    if (!isNaN(index)) {
      this.#goToSlide(index);
    }
  }

  toggleMute(event) {
    event.stopPropagation();
    const card = event.target.closest('[data-reel-card]');
    if (!card) return;
    const index = this._cards.indexOf(card);
    if (index === -1) return;

    // Toggle this specific card's audio without moving the carousel.
    // Unmuting a card automatically mutes whichever other card was active.
    this.#activeAudioIndex = this.#activeAudioIndex === index ? null : index;
    this.#updatePlaybackStates();
  }

  togglePlayPauseOnCard(event) {
    // Avoid triggering when clicking links or the mute toggle button
    if (event.target.closest('a, [data-reel-mute-toggle]')) return;
    event.stopPropagation();
    const card = event.target.closest('[data-reel-card]');
    if (card) {
      const index = this._cards.indexOf(card);
      if (index !== -1) {
        if (index !== this.#currentIndex) {
          this.#goToSlide(index);
        } else {
          this.#togglePlayPause(card);
        }
      }
    }
  }

  // =============== NAVIGATION ===============

  /** @param {number} direction - -1 for prev, 1 for next */
  #navigate(direction) {
    const infinite = this.dataset.infinite === 'true';
    const total = this._cards.length;
    let newIndex = this.#currentIndex + direction;

    if (infinite) {
      if (newIndex < 0) newIndex = total - 1;
      if (newIndex >= total) newIndex = 0;
    } else {
      newIndex = Math.max(0, Math.min(newIndex, total - 1));
    }

    this.#goToSlide(newIndex);
  }

  /** @param {number} index */
  #goToSlide(index) {
    if (!this._track || !this._cards[index]) return;
    this.#currentIndex = index;
    this.#isScrollingProgrammatically = true;

    // Temporarily disable scroll snap and smooth scrolling to prevent browser conflicts
    this._track.style.scrollSnapType = 'none';
    this._track.style.scrollBehavior = 'auto';

    const trackRect = this._track.getBoundingClientRect();
    const cardRect = this._cards[index].getBoundingClientRect();
    const scrollLeft = this._track.scrollLeft + (cardRect.left - trackRect.left);

    this._track.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });

    this.#updateControls();
    this.#updatePlaybackStates();

    // Re-enable scroll snap after scroll animation ends
    const checkScrollEnd = () => {
      const currentScrollLeft = this._track.scrollLeft;
      if (Math.abs(currentScrollLeft - scrollLeft) < 2) {
        this._track.style.scrollSnapType = '';
        this._track.style.scrollBehavior = '';
        this.#isScrollingProgrammatically = false;
      } else {
        requestAnimationFrame(checkScrollEnd);
      }
    };
    
    requestAnimationFrame(checkScrollEnd);
    setTimeout(() => {
      this._track.style.scrollSnapType = '';
      this._track.style.scrollBehavior = '';
      this.#isScrollingProgrammatically = false;
    }, 500);
  }

  #onScrollEnd() {
    if (!this._track || this.#isScrollingProgrammatically) return;

    const trackRect = this._track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    this._cards.forEach((card, i) => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    this.#currentIndex = closestIndex;
    this.#updateControls();
    this.#updatePlaybackStates();
  }

  #updateControls() {
    const infinite = this.dataset.infinite === 'true';
    const total = this._cards.length;

    // Arrows
    if (this._prevBtn) {
      this._prevBtn.toggleAttribute('disabled', !infinite && this.#currentIndex <= 0);
    }
    if (this._nextBtn) {
      this._nextBtn.toggleAttribute('disabled', !infinite && this.#currentIndex >= total - 1);
    }

    // Dots
    this._dots.forEach((dot, i) => {
      dot.classList.toggle('video-reels__dot--active', i === this.#currentIndex);
      dot.setAttribute('aria-current', i === this.#currentIndex ? 'true' : 'false');
    });

    // Counter
    if (this._counter) {
      this._counter.textContent = `${this.#currentIndex + 1} / ${total}`;
    }
  }

  // =============== CAROUSEL AUTOPLAY (auto-advance) ===============

  #startAutoAdvance() {
    if (this.dataset.carouselAutoplay !== 'true' || this.#isUserInteracting) return;
    this.#stopAutoAdvance();
    const speed = parseInt(this.dataset.carouselSpeed || '5', 10) * 1000;
    this.#autoAdvanceTimer = window.setInterval(() => {
      this.#navigate(1);
    }, speed);
  }

  #stopAutoAdvance() {
    if (this.#autoAdvanceTimer) {
      clearInterval(this.#autoAdvanceTimer);
      this.#autoAdvanceTimer = undefined;
    }
  }

  // =============== VIDEO CONTROL ===============

  /** @param {HTMLElement} card */
  #loadVideo(card) {
    const template = card.querySelector('template[data-video-template]');
    if (!template || card.dataset.videoLoaded === 'true') return;

    const content = /** @type {HTMLTemplateElement} */ (template).content.cloneNode(true);
    card.querySelector('[data-video-container]')?.appendChild(content);
    card.dataset.videoLoaded = 'true';
  }

  /** @param {HTMLElement} card */
  #playVideo(card) {
    // Ensure video is loaded first
    if (card.dataset.videoLoaded !== 'true') {
      this.#loadVideo(card);
    }

    const video = card.querySelector('video');
    if (video) {
      // Unmute only if this card is the one with active audio
      const index = this._cards.indexOf(card);
      video.muted = this.#activeAudioIndex !== index;

      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(() => {
          // Autoplay was prevented — try muted
          video.muted = true;
          video.play().catch(() => {});
        });
      }
      card.dataset.playing = 'true';
      this.#updatePlayPauseIcon(card, true);
    }

    // For iframes (YouTube/Vimeo), send play command
    const iframe = card.querySelector('iframe[data-video-type]');
    if (iframe) {
      const type = iframe.dataset.videoType;
      if (type === 'youtube') {
        iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      } else if (type === 'vimeo') {
        iframe.contentWindow?.postMessage('{"method":"play"}', '*');
      }

      // Sync mute state to the autoplaying iframe
      const index = this._cards.indexOf(card);
      this.#setCardMuteState(card, this.#activeAudioIndex !== index);

      card.dataset.playing = 'true';
      this.#updatePlayPauseIcon(card, true);
    }
  }

  /** @param {HTMLElement} card */
  #pauseVideo(card) {
    const video = card.querySelector('video');
    if (video) {
      video.pause();
      card.dataset.playing = 'false';
      this.#updatePlayPauseIcon(card, false);
    }

    const iframe = card.querySelector('iframe[data-video-type]');
    if (iframe) {
      const type = iframe.dataset.videoType;
      if (type === 'youtube') {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (type === 'vimeo') {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
      card.dataset.playing = 'false';
      this.#updatePlayPauseIcon(card, false);
    }
  }

  /** @param {HTMLElement} card */
  #togglePlayPause(card) {
    if (card.dataset.playing === 'true') {
      this.#pauseVideo(card);
    } else {
      this.#playVideo(card);
    }
  }

  /**
   * Sets the mute state of a card (both HTML5 video and iframes)
   * @param {HTMLElement} card
   * @param {boolean} shouldMute
   */
  #setCardMuteState(card, shouldMute) {
    const video = card.querySelector('video');
    if (video) {
      video.muted = shouldMute;
    }

    const iframe = card.querySelector('iframe[data-video-type]');
    if (iframe) {
      const type = iframe.dataset.videoType;
      if (type === 'youtube') {
        const func = shouldMute ? 'mute' : 'unmute';
        iframe.contentWindow?.postMessage(`{"event":"command","func":"${func}","args":""}`, '*');
      } else if (type === 'vimeo') {
        const method = shouldMute ? 'mute' : 'unmute';
        iframe.contentWindow?.postMessage(`{"method":"${method}"}`, '*');
      }
    }

    // Update mute icon visual state on this specific card
    const muteBtn = card.querySelector('[data-reel-mute-toggle]');
    if (muteBtn) {
      const volIcon = muteBtn.querySelector('[data-icon-volume]');
      const muteIcon = muteBtn.querySelector('[data-icon-muted]');
      if (volIcon) volIcon.classList.toggle('hidden', shouldMute);
      if (muteIcon) muteIcon.classList.toggle('hidden', !shouldMute);
    }
  }

  /**
   * Ensures only the card with active audio plays sound,
   * while every other card is strictly muted.
   */
  #updatePlaybackStates() {
    this._cards.forEach((card, index) => {
      const shouldMute = this.#activeAudioIndex !== index;
      this.#setCardMuteState(card, shouldMute);
    });
  }

  /** @param {HTMLElement} card */
  #toggleMute(card) {
    const index = this._cards.indexOf(card);
    if (index === -1) return;
    this.#activeAudioIndex = this.#activeAudioIndex === index ? null : index;
    this.#updatePlaybackStates();
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} isPlaying
   */
  #updatePlayPauseIcon(card, isPlaying) {
    const btn = card.querySelector('[data-reel-play-toggle]');
    if (!btn) return;
    const playIcon = btn.querySelector('[data-icon-play]');
    const pauseIcon = btn.querySelector('[data-icon-pause]');
    if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
    if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
  }

  // =============== KEYBOARD ===============

  /** @param {KeyboardEvent} e */
  #handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.#navigate(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.#navigate(1);
        break;
      case ' ':
      case 'Enter': {
        e.preventDefault();
        const activeCard = this._cards[this.#currentIndex];
        if (activeCard) this.#togglePlayPause(activeCard);
        break;
      }
      case 'm':
      case 'M': {
        const activeCard = this._cards[this.#currentIndex];
        if (activeCard) this.#toggleMute(activeCard);
        break;
      }
    }
  };
}

if (!customElements.get('video-reels-component')) {
  customElements.define('video-reels-component', VideoReelsComponent);
}